'use strict';

import React, {useState, useCallback, useEffect} from 'react';
import {BrowserRouter as Router, Link, Switch, Route, useRouteMatch} from 'react-router-dom';

import {cls} from '@zerolight/cloudmine-api-client';

const {Filter: {Filter}} = cls;

import auth from '../shared/auth';
import {cssClasses} from '../shared/utils';
import config from '../shared/config';
import ControllerWrapper from "./controller-wrapper";

import Dashboard3D from '../pages/dashboard';
import Dashboard2D from "../pages/dashboard2d";
import Browsers from '../pages/browsers';
import StatsPage from '../pages/stats';
import {MinutesChart, CCUChart, StreamsChart} from '../pages/charts';
import ServiceConfigurationPage from "../pages/service-config";

function usePanelPageRoute({page, path, setActive}) {
	const ShowPagesFilter = new Filter(config.get('ShowPages'));
	const show = !page || ShowPagesFilter.matches(page);
	const match = useRouteMatch({path, exact: true});
	useEffect(() => {
		if (match && setActive) {
			setActive();
		}
	}, [match, setActive]);
	return [show, match];
}

function TopLevelLink({page, path, setActive, children}) {
	const [show, match] = usePanelPageRoute({page, path, setActive});
	if (!show) {
		return null;
	}
	return (
		<li role="navigation" className={cssClasses({active: match})}>
			<Link to={path}>{children}</Link>
		</li>
	);
}

function DropDownLink({page, path, setActive, children}) {
	const [show, match] = usePanelPageRoute({page, path, setActive});
	if (!show) {
		return null;
	}
	return (
		<li role="navigation" className={cssClasses({active: match})}>
			<Link to={path}>{children}</Link>
		</li>
	);
}

function DropDown({label, active, children}) {
	return (
		<li role="presentation" className={cssClasses('dropdown', {active})}>
			<a className="dropdown-toggle" data-toggle="dropdown" href="#" role="button">
				{label} <span className="caret"/>
			</a>
			<ul className="dropdown-menu">
				{children}
			</ul>
		</li>
	);
}

function PanelRouter() {
	const [active, setActive] = useState('dashboard');

	const setDashboard = useCallback(setActive.bind(null, 'dashboard'), [setActive]);
	const setDashboard2d = useCallback(setActive.bind(null, 'dashboard2d'), [setActive]);
	const setKPI = useCallback(setActive.bind(null, 'kpi'), [setActive]);
	const setCharts = useCallback(setActive.bind(null, 'charts'), [setActive]);
	const setStats = useCallback(setActive.bind(null, 'stats'), [setActive]);

	const {ServiceConfiguration} = config.data;

	return (
		<Router>
			<ul className="nav nav-pills" id="cm-navigation">
				<TopLevelLink page="Dashboard" path="/" setActive={setDashboard}>Dashboard</TopLevelLink>
				<TopLevelLink page="Dashboard2d" path="/dashboard-2d" setActive={setDashboard2d}>2D Usage</TopLevelLink>
				<DropDown label="KPI" active={active === 'kpi'}>
					<DropDownLink page="Browsers" path="/browsers" setActive={setKPI}>Browser stats</DropDownLink>
				</DropDown>
				<DropDown label="Charts" active={active === 'charts'}>
					<DropDownLink path="/charts-minutes" setActive={setCharts}>Minutes</DropDownLink>
					<DropDownLink path="/charts-streams" setActive={setCharts}>Streams</DropDownLink>
					<DropDownLink path="/charts-ccu" setActive={setCharts}>CCU</DropDownLink>
				</DropDown>
				<TopLevelLink page="Stats" path="/stats" setActive={setStats}>Stats</TopLevelLink>
				<li role="presentation">
					<a className="dropdown-toggle" data-toggle="dropdown" href="#" role="button">
						<i className="fas fa-cog"/>
					</a>
					<ul className="dropdown-menu">
						{
							ServiceConfiguration &&
							(
								<DropDownLink page="ServiceConfiguration" path="/service-configuration">
									Service configuration
								</DropDownLink>
							)
						}
						<li role="navigation">
							<a href="#" onClick={
								(e) => {
									e.stopPropagation();
									e.preventDefault();
									auth.signOut();
								}
							}>Sign out</a></li>
					</ul>
				</li>
			</ul>

			<section id="cm-page">
				<Switch>
					<Route path="/dashboard-3d"><Dashboard3D/></Route>
					<Route path="/dashboard-2d"><Dashboard2D/></Route>
					<Route path="/browsers">{ControllerWrapper.wrap(Browsers)}</Route>
					<Route path="/charts-minutes">{ControllerWrapper.wrap(MinutesChart)}</Route>
					<Route path="/charts-streams">{ControllerWrapper.wrap(StreamsChart)}</Route>
					<Route path="/charts-ccu">{ControllerWrapper.wrap(CCUChart)}</Route>
					<Route path="/stats"><StatsPage/></Route>
					{
						ServiceConfiguration &&
						<Route path="/service-configuration"><ServiceConfigurationPage/></Route>
					}
					<Route path="/" exact><Dashboard3D/></Route>
				</Switch>
			</section>
		</Router>
	);
}


export default PanelRouter;